import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import { DEFAULT_TRACKING } from '@/lib/text/constants/text-defaults'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { TRACKING_MAX, TRACKING_MIN, TRACKING_PRESET_VALUES } from '../constants/typography-ranges'
import {
  exceedsSpacingInputLimit,
  resolveSpacingCommit,
  spacingSliderValueText,
  validateSpacingInput,
} from '../helpers/spacing-input'
import {
  isTrackingPresetValue,
  nearestTrackingLabel,
  presetValueForLabel,
  resolvePresetChipLabel,
} from '../helpers/spacing-presets'
import { useSliderCommitGuard } from './use-slider-commit-guard'

const TRACKING_INPUT_RULES = {
  min: TRACKING_MIN,
  max: TRACKING_MAX,
  rangeMessage: `Value: ${TRACKING_MIN}–${TRACKING_MAX}`,
  strict: true,
}

interface TrackingFieldOptions {
  disabled: boolean
  hasTextSelectionTarget: boolean
  emitStyleUpdate: (update: TextRunStyleUpdate) => void
  restoreActiveEditor: () => void
}

/** Tracking: a range/whole-box control, disabled at a collapsed caret (Kerning's territory). */
export function useTrackingField({
  disabled,
  hasTextSelectionTarget,
  emitStyleUpdate,
  restoreActiveEditor,
}: TrackingFieldOptions) {
  const [tracking, setTracking] = useState([DEFAULT_TRACKING])
  const [trackingInputValue, setTrackingInputValue] = useState<string>(String(DEFAULT_TRACKING))
  const [trackingMixed, setTrackingMixed] = useState(false)
  const [trackingError, setTrackingError] = useState(false)
  const [trackingErrorMsg, setTrackingErrorMsg] = useState<string>('')
  const trackingInputFocused = useRef(false)
  const trackingLastValidInputRef = useRef<string>(String(DEFAULT_TRACKING))
  // True while actively live-editing shape text with a collapsed caret (no real, non-collapsed
  // text range selected) — Tracking is a range/whole-box control, so a collapsed caret must
  // disable it just as much as Kerning being actionable does. `kerningAvailable` alone used to
  // stand in for "caret is collapsed", but it's false both when the caret is collapsed-without-a-
  // valid-glyph-pair (e.g. at the very start/end of the text) AND when there's a real range
  // selected — only this flag distinguishes the two. False whenever not live-editing at all (the
  // Select-Tool whole-box path governs Tracking through `hasTextSelectionTarget` instead).
  const [liveEditingCollapsedCaret, setLiveEditingCollapsedCaret] = useState(false)

  // Kerning (caret) and Tracking (range/whole-box) are mutually exclusive: a collapsed caret —
  // whether or not it has a valid Kerning glyph pair — must disable Tracking, even though
  // `hasTextSelectionTarget` is still true for that same caret. `liveEditingCollapsedCaret` is
  // false while not live-editing at all (the Select-Tool whole-box path, where Tracking applies to
  // the entire selection regardless of any "caret"), so this only takes effect during live editing.
  const trackingControlsDisabled = disabled || !hasTextSelectionTarget || liveEditingCollapsedCaret

  const sliderValueText = spacingSliderValueText(tracking[0], 'thousandths of an em', {
    mixed: trackingMixed,
    unavailable: trackingControlsDisabled,
  })

  const chipValue = resolvePresetChipLabel(
    trackingInputValue,
    trackingMixed,
    isTrackingPresetValue,
    nearestTrackingLabel
  )

  const commitTracking = (value: number) => {
    setTracking([value])
    setTrackingInputValue(String(value))
    trackingLastValidInputRef.current = String(value)
    setTrackingMixed(false)
    setTrackingError(false)
    setTrackingErrorMsg('')
    emitStyleUpdate({ tracking: value })
  }

  const handleChipChange = (val: string) => {
    const v = presetValueForLabel(val, TRACKING_PRESET_VALUES)

    if (v !== undefined) commitTracking(v)

    restoreActiveEditor()
  }

  const sliderGuard = useSliderCommitGuard(trackingInputFocused, restoreActiveEditor)

  const handleSliderChange = (val: number[]) => {
    // Same guard the numeric input uses on focus — without it, every drag tick bumps
    // textPanelRevision, which re-fires the selection-sync effect and immediately
    // overwrites this value with the (already-applied) stored value, so the thumb can
    // only ever move one step before appearing stuck.
    trackingInputFocused.current = true
    commitTracking(val[0])
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value

    // 3 whole digits and 2 decimals (legacy limit) — rejecting the change keeps the previous text.
    if (exceedsSpacingInputLimit(raw)) return

    setTrackingInputValue(raw)

    // Validation only — nothing is applied until Enter/blur (`handleInputBlur`), so an
    // intermediate keystroke ("-15" on the way to "-150") never becomes the last valid value.
    const result = validateSpacingInput(raw, TRACKING_INPUT_RULES)

    if (result.kind === 'partial' || result.kind === 'valid') {
      setTrackingError(false)
      setTrackingErrorMsg('')
      return
    }

    setTrackingError(true)
    setTrackingErrorMsg(result.message)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.ctrlKey || e.metaKey || e.altKey) return

    // Commit-or-revert on Enter the same way blur does (Kerning does the same).
    e.preventDefault()
    ;(e.target as HTMLInputElement).blur()
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    trackingInputFocused.current = true
    e.target.select()
  }

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    trackingInputFocused.current = false

    const committed = resolveSpacingCommit(e.target.value, TRACKING_INPUT_RULES)
    const lastValid = trackingLastValidInputRef.current
    const lastValidN = parseFloat(lastValid)

    if (committed !== null && committed !== lastValidN) {
      commitTracking(committed)
    } else {
      // Blank, '-', out of range or unchanged: restore the last committed value — the canvas was
      // never touched while typing — and clear the error. Also normalizes "10." to "10".
      setTrackingInputValue(lastValid)
      setTracking([isFinite(lastValidN) ? lastValidN : DEFAULT_TRACKING])
      setTrackingError(false)
      setTrackingErrorMsg('')
    }

    restoreActiveEditor()
  }

  /** Shows a value on the slider and input as the committed value, clearing any error. */
  const showTracking = useCallback((sliderValue: number, inputText: string, mixed: boolean) => {
    setTracking([sliderValue])
    setTrackingInputValue(inputText)
    trackingLastValidInputRef.current = inputText
    setTrackingMixed(mixed)
    setTrackingError(false)
    setTrackingErrorMsg('')
  }, [])

  /** Live text editing: show the editor's selection style, unless the input is being edited. */
  const syncFromLiveEdit = useCallback(
    (style: TextRunStyleUpdate | null | undefined) => {
      if (!style || trackingInputFocused.current) return

      if (style.mixedTracking) {
        // Park the thumb at the slider's minimum (like Leading's mixed state) rather than
        // the Natural (0) position, so mixed selections read as "no single value" visually.
        showTracking(TRACKING_MIN, '', true)
      } else if (typeof style.tracking === 'number') {
        showTracking(style.tracking, String(style.tracking), false)
      } else {
        // No tracking has ever been applied at the caret — show the Natural default (0),
        // not a blank placeholder, so the Natural chip and slider reflect the true value.
        showTracking(DEFAULT_TRACKING, String(DEFAULT_TRACKING), false)
      }
    },
    [showTracking]
  )

  /** Select-Tool whole-box selection: `value` is the first run's tracking. */
  const syncFromParagraphs = useCallback(
    (mixed: boolean, value: unknown) => {
      setTrackingMixed(mixed)
      if (trackingInputFocused.current) return

      if (mixed) {
        // Park the thumb at the slider's minimum (like Leading's mixed state) rather than
        // the Natural (0) position, so mixed selections read as "no single value" visually.
        showTracking(TRACKING_MIN, '', true)
      } else if (typeof value === 'number') {
        showTracking(value, String(value), false)
      } else {
        showTracking(DEFAULT_TRACKING, String(DEFAULT_TRACKING), false)
      }
    },
    [showTracking]
  )

  /** Nothing selected: blank input, Natural thumb. */
  const resetTracking = useCallback(() => {
    showTracking(DEFAULT_TRACKING, '', false)
  }, [showTracking])

  return {
    tracking,
    trackingInputValue,
    trackingMixed,
    trackingError,
    trackingErrorMsg,
    trackingControlsDisabled,
    chipValue,
    sliderValueText,
    sliderGuard,
    setLiveEditingCollapsedCaret,
    handleChipChange,
    handleSliderChange,
    handleInputChange,
    handleInputKeyDown,
    handleInputFocus,
    handleInputBlur,
    syncFromLiveEdit,
    syncFromParagraphs,
    resetTracking,
  }
}

export type TrackingField = ReturnType<typeof useTrackingField>
